import React, { useState, useMemo } from "react";
import { Alert, Snackbar } from "@mui/material";
import UtilsFormule from "../../../utils/UtilsFormule";
import uploadAndDownloadService from "../../../services/uploadAndDownloadFile";
import * as XLSX from "xlsx";

const fmt = (v) => parseFloat(Number(v || 0).toFixed(3));

// ── helpers ──────────────────────────────────────────────────────────────────

function getLibelle(row) {
  if (row.factureSale)        return `Facture vente — ${row.factureSale.ref} ${row.factureSale.title || ""}`;
  if (row.facture)            return `Facture achat — ${row.facture.ref} ${row.facture.title || ""}`;
  if (row.retenuSource)       return "Retenue à la source";
  if (row.payementInformation) return getLibellePayement(row.payementInformation);
  return "—";
}

function getLibellePayement(p) {
  if (!p) return "Paiement";
  switch (p.typePayement) {
    case "Virement":         return `Virement — ${p.numVirement || ""}`;
    case "Wallet":           return `Wallet — ${p.wallet?.title || ""}`;
    case "Chéque / Traites": return `Chèque / Traite — ${p.codeCheque || ""}`;
    case "Espéce":           return `Espèces — ${p.ref || ""}/${p.year || ""}`;
    case "En ligne":         return `TPE — ${p.ref || ""}`;
    default:                 return `Paiement — ${p.typePayement || ""}`;
  }
}

function getTypeLabel(type) {
  if (type === 0) return { label: "Crédit",  color: "#1b5e20" };
  if (type === 1) return { label: "Débit",   color: "#b71c1c" };
  if (type === 2) return { label: "Retenu",  color: "#e65100" };
  return { label: "—", color: "#555" };
}

// For tiersAccount.type 2 or 3, the transaction type isn't reliable from the
// backend: it's derived from the libellé instead — Wallet transactions are
// débit, everything else is crédit.
function resolveType(row, tierAccount) {
  if (tierAccount && (tierAccount.type === 2 || tierAccount.type === 3)) {
    return getLibelle(row).startsWith("Wallet") ? 1 : 0;
  }
  return row.type;
}

// ── component ─────────────────────────────────────────────────────────────────

const DataTableAccountTransactions = ({ listTransaction, tierAccount }) => {
  const [search,        setSearch]        = useState("");
  const [openInfoAlert, setOpenInfoAlert] = useState(false);
  const [infoAlertText, setInfoAlertText] = useState("");

  // transactions already sorted DESC (most recent first) by parent
  // compute running balance: start from current solde and go backwards
  const rowsWithBalance = useMemo(() => {
    const currentSolde = Number(tierAccount?.solde || 0);
    let running = currentSolde;
    return listTransaction.map((t) => {
      const soldeApres = running;
      const effectiveType = resolveType(t, tierAccount);
      if (effectiveType === 0) {
        running = running - Number(t.amount || 0);   // crédit → avant c'était moins
      } else {
        running = running + Number(t.amount || 0);   // débit  → avant c'était plus
      }
      return { ...t, soldeApres, effectiveType };
    });
  }, [listTransaction, tierAccount]);

  // totaux
  const totalCredit = rowsWithBalance.filter(t => t.effectiveType === 0).reduce((s, t) => s + Number(t.amount || 0), 0);
  const totalDebit  = rowsWithBalance.filter(t => t.effectiveType !== 0).reduce((s, t) => s + Number(t.amount || 0), 0);

  // filtre recherche
  const filtered = useMemo(() => {
    if (!search.trim()) return rowsWithBalance;
    const q = search.toLowerCase();
    return rowsWithBalance.filter((r) => {
      return (
        getLibelle(r).toLowerCase().includes(q) ||
        UtilsFormule.formatDateHours(r.creationDate).toLowerCase().includes(q) ||
        String(r.amount).includes(q) ||
        getTypeLabel(r.effectiveType).label.toLowerCase().includes(q)
      );
    });
  }, [rowsWithBalance, search]);

  // ── export Excel ──
  const handleExport = () => {
    const data = rowsWithBalance.map((r) => ({
      "Date":         UtilsFormule.formatDateHours(r.creationDate),
      "Libellé":      getLibelle(r),
      "Type":         getTypeLabel(r.effectiveType).label,
      "Débit":        r.effectiveType !== 0 ? fmt(r.amount) : "",
      "Crédit":       r.effectiveType === 0 ? fmt(r.amount) : "",
      "Solde après":  fmt(r.soldeApres),
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Extrait");
    XLSX.writeFile(wb, `extrait_compte_${new Date().toISOString().split("T")[0]}.xlsx`);
  };

  // ── fichier paiement ──
  const openFile = (path) => {
    if (!path || path === "any") return;
    uploadAndDownloadService.downloadFile(path).then((res) => {
      const type = res.headers["content-type"];
      const file = new Blob([res.data], { type });
      const url  = URL.createObjectURL(file);
      window.open(url);
    });
  };

  const openFacture = (ref, id) => {
    sessionStorage.setItem("refDocument", ref);
    sessionStorage.setItem("typeDocument", "facture");
    localStorage.setItem("idBill", id);
    window.open("/payerBillVente");
  };

  return (
    <>
      <style>{`
        .eb-wrap { font-family: 'Source Sans Pro', Arial, sans-serif; }
        .eb-header {
          background: #111;
          color: #fff;
          border-radius: 4px 4px 0 0;
          padding: 18px 24px 14px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 10px;
        }
        .eb-header-title { font-size: 1.1rem; font-weight: 700; letter-spacing: 1px; }
        .eb-header-sub   { font-size: 0.8rem; color: #aaa; margin-top: 2px; }
        .eb-totals {
          background: #f9f9f9;
          border: 1px solid #e0e0e0;
          border-top: none;
          padding: 10px 24px;
          display: flex;
          gap: 32px;
          flex-wrap: wrap;
          align-items: center;
        }
        .eb-total-item { font-size: 0.85rem; color: #555; }
        .eb-total-item strong { font-size: 1rem; }
        .eb-toolbar {
          background: #fff;
          border: 1px solid #e0e0e0;
          border-top: none;
          padding: 10px 24px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          flex-wrap: wrap;
        }
        .eb-search {
          border: 1.5px solid #ccc;
          border-radius: 3px;
          padding: 6px 12px;
          font-size: 0.85rem;
          outline: none;
          width: 220px;
        }
        .eb-search:focus { border-color: #111; }
        .eb-table-wrap {
          border: 1px solid #e0e0e0;
          border-top: none;
          border-radius: 0 0 4px 4px;
          overflow: hidden;
        }
        .eb-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.875rem;
        }
        .eb-table thead tr {
          background: #222;
          color: #fff;
        }
        .eb-table thead th {
          padding: 10px 14px;
          font-weight: 600;
          letter-spacing: 0.5px;
          font-size: 0.8rem;
          white-space: nowrap;
        }
        .eb-table tbody tr {
          border-bottom: 1px solid #f0f0f0;
          transition: background 0.15s;
        }
        .eb-table tbody tr:hover { background: #f5f5f5 !important; }
        .eb-table tbody td {
          padding: 9px 14px;
          vertical-align: middle;
        }
        .eb-row-credit { background: #f0fff4; }
        .eb-row-debit  { background: #fff5f5; }
        .eb-row-retenu { background: #fff8f0; }
        .eb-date    { color: #555; font-size: 0.8rem; white-space: nowrap; }
        .eb-libelle { color: #222; font-weight: 500; }
        .eb-badge {
          display: inline-block;
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 0.72rem;
          font-weight: 700;
          letter-spacing: 0.5px;
          text-transform: uppercase;
        }
        .eb-badge-credit { background: #c8e6c9; color: #1b5e20; }
        .eb-badge-debit  { background: #ffcdd2; color: #b71c1c; }
        .eb-badge-retenu { background: #ffe0b2; color: #e65100; }
        .eb-amount-credit { color: #1b5e20; font-weight: 700; text-align: right; }
        .eb-amount-debit  { color: #b71c1c; font-weight: 700; text-align: right; }
        .eb-amount-empty  { color: #ccc; text-align: right; }
        .eb-solde-pos { color: #1b5e20; font-weight: 700; text-align: right; white-space: nowrap; }
        .eb-solde-neg { color: #b71c1c; font-weight: 700; text-align: right; white-space: nowrap; }
        .eb-action-btn {
          background: none;
          border: 1px solid #ccc;
          border-radius: 3px;
          padding: 3px 8px;
          cursor: pointer;
          font-size: 0.78rem;
          color: #555;
          transition: background 0.15s;
        }
        .eb-action-btn:hover { background: #eee; }
        .eb-empty { text-align: center; padding: 40px; color: #999; font-size: 0.9rem; }
        .eb-count { font-size: 0.8rem; color: #888; }
      `}</style>

      <div className="eb-wrap">

        {/* ── Header ── */}
        <div className="eb-header">
          <div>
            <div className="eb-header-title">
              <i className="fas fa-landmark mr-2"></i>
              Extrait de compte
            </div>
            <div className="eb-header-sub">
              {listTransaction.length} transaction{listTransaction.length > 1 ? "s" : ""} —
              solde actuel :&nbsp;
              <strong style={{ color: Number(tierAccount?.solde || 0) >= 0 ? "#a5d6a7" : "#ef9a9a" }}>
                {fmt(tierAccount?.solde)} DNT
              </strong>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button className="btn btn-sm btn-outline-light" onClick={handleExport}>
              <i className="fas fa-file-excel mr-1"></i> Excel
            </button>
          </div>
        </div>

        {/* ── Totaux ── */}
        <div className="eb-totals">
          <div className="eb-total-item">
            <span style={{ color: "#888" }}>Total crédits : </span>
            <strong style={{ color: "#1b5e20" }}>+{fmt(totalCredit)} DNT</strong>
          </div>
          <div className="eb-total-item">
            <span style={{ color: "#888" }}>Total débits : </span>
            <strong style={{ color: "#b71c1c" }}>-{fmt(totalDebit)} DNT</strong>
          </div>
          <div className="eb-total-item">
            <span style={{ color: "#888" }}>Solde net : </span>
            <strong style={{ color: fmt(totalCredit - totalDebit) >= 0 ? "#1b5e20" : "#b71c1c" }}>
              {fmt(totalCredit - totalDebit)} DNT
            </strong>
          </div>
        </div>

        {/* ── Toolbar ── */}
        <div className="eb-toolbar">
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <i className="fas fa-search" style={{ color: "#aaa" }}></i>
            <input
              className="eb-search"
              type="text"
              placeholder="Rechercher..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <span className="eb-count">
            {filtered.length} / {rowsWithBalance.length} entrées
          </span>
        </div>

        {/* ── Table ── */}
        <div className="eb-table-wrap">
          <table className="eb-table">
            <thead>
              <tr>
                <th style={{ width: "13%" }}>Date</th>
                <th style={{ width: "30%" }}>Libellé</th>
                <th style={{ width: "10%" }}>Type</th>
                <th style={{ width: "12%", textAlign: "right" }}>Débit</th>
                <th style={{ width: "12%", textAlign: "right" }}>Crédit</th>
                <th style={{ width: "13%", textAlign: "right" }}>Solde</th>
                <th style={{ width: "10%", textAlign: "center" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="eb-empty">
                    <i className="fas fa-inbox mr-2"></i>
                    Aucune transaction trouvée.
                  </td>
                </tr>
              )}
              {filtered.map((row, i) => {
                const isCredit = row.effectiveType === 0;
                const isRetenu = row.effectiveType === 2;
                const rowClass = isCredit ? "eb-row-credit" : isRetenu ? "eb-row-retenu" : "eb-row-debit";
                const badgeClass = isCredit ? "eb-badge-credit" : isRetenu ? "eb-badge-retenu" : "eb-badge-debit";
                const { label } = getTypeLabel(row.effectiveType);

                return (
                  <tr key={row.id || i} className={rowClass}>
                    {/* Date */}
                    <td className="eb-date">
                      {UtilsFormule.formatDateHours(row.creationDate)}
                    </td>

                    {/* Libellé */}
                    <td className="eb-libelle">
                      {getLibelle(row)}
                    </td>

                    {/* Type badge */}
                    <td>
                      <span className={`eb-badge ${badgeClass}`}>{label}</span>
                    </td>

                    {/* Débit */}
                    <td className={isCredit ? "eb-amount-empty" : "eb-amount-debit"}>
                      {!isCredit ? `−${fmt(row.amount)}` : ""}
                    </td>

                    {/* Crédit */}
                    <td className={isCredit ? "eb-amount-credit" : "eb-amount-empty"}>
                      {isCredit ? `+${fmt(row.amount)}` : ""}
                    </td>

                    {/* Solde progressif */}
                    <td className={row.soldeApres >= 0 ? "eb-solde-pos" : "eb-solde-neg"}>
                      {fmt(row.soldeApres)} DNT
                    </td>

                    {/* Action */}
                    <td style={{ textAlign: "center" }}>
                      {row.factureSale && (
                        <button
                          className="eb-action-btn"
                          title="Voir facture"
                          onClick={() => openFacture(row.factureSale.ref, row.factureSale.id)}
                        >
                          <i className="fas fa-file-invoice"></i>
                        </button>
                      )}
                      {row.payementInformation?.urlFile && row.payementInformation.urlFile !== "any" && (
                        <button
                          className="eb-action-btn ml-1"
                          title="Voir pièce jointe"
                          onClick={() => openFile(row.payementInformation.urlFile)}
                        >
                          <i className="fas fa-paperclip"></i>
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {openInfoAlert && (
        <Snackbar
          open={openInfoAlert}
          autoHideDuration={2000}
          onClose={() => setOpenInfoAlert(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <Alert variant="filled" onClose={() => setOpenInfoAlert(false)} severity="info">
            {infoAlertText}
          </Alert>
        </Snackbar>
      )}
    </>
  );
};

export default DataTableAccountTransactions;
